/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import {
    DEFAULT_CURSOR,
    SHOW_ON_MOBILE,
    HIDE_ON_MOBILE,
    generatePaginationInfo,
    ELLIPSES,
    generateClassNamesForStyling,
} from './helpers'
import { ChevronLeftIcon } from '../icons/ChevronLeftIcon'
import { ChevronRightIcon } from '../icons/ChevronRightIcon'

export interface PageSelectorProps {
    /**
     * The classname -- this is mostly used to hook GA event tracking
     */
    className?: string

    /**
     * The number of items to display per page
     */
    itemsPerPage: number

    /**
     * The total number of items
     */
    totalItems: number

    /**
     * The number of the currently selected page of items
     */
    selectedPage: number

    /**
     * Text to describe items being shown on page
     */
    itemTypeName?: string

    /**
     * The function to be called when a selecting a new page.
     */
    onPageSelect: (selectedPage: number) => void

    /**
     * This makes it possible to pass through additional props.
     */
    [propName: string]: any

    showPagination?: boolean
}

export function PageSelector({
    className = 'js_pageSelector',
    itemsPerPage,
    totalItems,
    selectedPage,
    itemTypeName,
    onPageSelect,
    showPagination = true,
    ...rest
}: PageSelectorProps) {
    const totalPages = Math.ceil(totalItems / itemsPerPage)

    if (selectedPage > totalPages) {
        selectedPage = totalPages
    }

    if (selectedPage < 1) {
        selectedPage = 1
    }

    const { pageNumbers } = generatePaginationInfo(selectedPage, totalPages)

    const pages = pageNumbers.map((page, i) => {
        const stringValue = page.toString()

        const parsedNumber = parseInt(stringValue, 10)
        const value = !isNaN(parsedNumber) ? parsedNumber : ELLIPSES

        const classNames = generateClassNamesForStyling(stringValue)

        const mobileClasses = classNames.includes(SHOW_ON_MOBILE)
            ? 'md:hidden'
            : ''
        const desktopclasses = classNames.includes(HIDE_ON_MOBILE)
            ? 'hidden md:inline-flex'
            : ''

        const elipsesClass = classNames.includes(DEFAULT_CURSOR)
            ? 'pointer-events-none'
            : ''

        return parseInt(stringValue, 10) === selectedPage ? (
            <a
                key={i}
                href="#"
                className={`border-state-focus bg-brand-white text-brand-black       relative z-10 inline-flex items-center border px-4 py-2 text-sm font-medium`}
                data-testid={'selected-pagenumber'}
                onClick={() => typeof value === 'number' && onPageSelect(value)}
            >
                {value}
            </a>
        ) : (
            <a
                key={i}
                href="#"
                className={`text-brand-black hover:bg-brand-white relative inline-flex items-center border border-neutral-700 bg-white px-4 py-2 text-sm font-medium ${desktopclasses} ${mobileClasses} ${elipsesClass}`}
                onClick={() => typeof value === 'number' && onPageSelect(value)}
            >
                {value}
            </a>
        )
    })

    const hideNextClass = selectedPage === totalPages ? `invisible` : ``
    const hidePrevClass = selectedPage === 1 ? `invisible` : ``

    return (
        <div
            aria-label="pagination"
            className={`flex items-center justify-between bg-white ${className}`}
            {...rest}
        >
            <div
                className={` sm:flex sm:flex-1 sm:items-center sm:justify-between`}
            >
                <div>
                    <p className={`text-brand-black pb-6 text-base`}>
                        Showing{' '}
                        <span className={`font-medium`}>
                            {(selectedPage - 1) * itemsPerPage + 1}
                        </span>{' '}
                        -{' '}
                        <span className={`font-medium`}>
                            {totalItems <= itemsPerPage
                                ? totalItems
                                : selectedPage * itemsPerPage}
                        </span>{' '}
                        of{' '}
                        <span className={`font-medium`}>
                            {totalItems.toLocaleString()}
                        </span>
                        {itemTypeName && <span> {itemTypeName}</span>}
                    </p>
                </div>
                {showPagination && (
                    <div>
                        <nav
                            className={`relative z-0 inline-flex -space-x-px rounded-md`}
                            aria-label="Pagination"
                        >
                            <a
                                aria-label="first"
                                href="#"
                                className={`text-brand-black hover:bg-brand-white relative inline-flex items-center rounded-l-md border border-neutral-700 bg-white px-2 py-2 text-sm font-medium ${hidePrevClass}`}
                                onClick={() => onPageSelect(1)}
                            >
                                <span className="sr-only">Previous</span>
                                <ChevronLeftIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                />
                            </a>
                            {pages}
                            <a
                                aria-label="last"
                                href="#"
                                className={`text-brand-black hover:bg-brand-white relative inline-flex items-center rounded-r-md border border-neutral-700 bg-white px-2 py-2 text-sm font-medium ${hideNextClass}`}
                                onClick={() => onPageSelect(totalPages)}
                            >
                                <span className="sr-only">Next</span>
                                <ChevronRightIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                />
                            </a>
                        </nav>
                    </div>
                )}
            </div>
        </div>
    )
}
